import { isChanged } from "proxy-compare";
import * as React from "react";
import { snapshot, Snapshot, subscribe } from "./valtioReexport";
import { createProxy as createProxyToCompare } from "proxy-compare";
import { captureException } from "@web-monorepo/telemetry";

/**! @preserve from https://github.com/pmndrs/valtio/blob/ce1d62183e4968d93174deaded4e32670cb44de6/src/react.ts */

// This is required only for performance.
// Ref: https://github.com/pmndrs/valtio/issues/519
const targetCache = new WeakMap<object, unknown>();

// We've copied this from the Valtio library to make one key change - eliminate
// the "sync/not-sync" distinction, always have the same behaviors:
//
// - If a change arrives _during the render cycle_, queue it and handle it after the render cycle.
// - If the change arrives _outside the render cycle_, immediately dispatch the change
//
// This is to solve the key "hard problem" with react state, that you've got to:
//
// - deliver changes immediately on input change events, or react/html will move your cursor to the
//   end of an input
// - not deliver changes during the render cycle
//
// Existing Valtio only lets you pick immediate or batched, there's no mode to pick which one
// is appropriate given the actual change.

export function useSnapshot<T extends object>(proxyObject: T): Snapshot<T> {
  if (!proxyObject) throw new Error("useSnapshot received a null or undefined proxyObject");

  // per-proxy & per-hook affected, it's not ideal but memo compatible
  const affected = React.useMemo(() => proxyObject && new WeakMap<object, unknown>(), [proxyObject]);
  const lastSnapshot = React.useRef<undefined | Snapshot<T>>(undefined);
  let inRender = true;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- we are using useMemo as a way to smartly keep/blow-away changes depending on if proxyObject changes.
  const renderBatchChanges = React.useMemo(() => ({ changes: null as null | Set<() => void> }), [proxyObject]);
  const renderTracker = React.useRef(true);
  renderTracker.current = true;
  const useSnapshotStack = React.useMemo(() => new Error().stack, []);

  const currSnapshot = React.useSyncExternalStore(
    React.useCallback(
      (callback) => {
        const unsub = subscribe(
          proxyObject,
          () => {
            if (renderTracker.current) {
              // (this is the main body of our change):
              // Inside a render, batch changes for after the render:
              if (renderBatchChanges.changes) {
                renderBatchChanges.changes.add(callback);
              } else {
                renderBatchChanges.changes = new Set([callback]);
                Promise.resolve()
                  .then(() => {
                    for (const batchChangeCb of renderBatchChanges.changes!) batchChangeCb();
                    renderBatchChanges.changes = null;
                  })
                  .catch(
                    /* istanbul ignore next -- @preserve defensive check */ (x) => {
                      captureException(x, { extra: { message: "error in useSnapshot", useSnapshotStack } });
                    },
                  );
              }
            } else {
              // outside of render, synchronous behavior
              callback();
            }
          },
          true,
        );
        callback();
        return unsub;
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps -- same thing as above, we want to only get a new callback when the proxy object changes.
      [proxyObject],
    ),
    () => {
      const nextSnapshot = snapshot(proxyObject);
      try {
        if (
          !inRender &&
          lastSnapshot.current &&
          !isChanged(lastSnapshot.current, nextSnapshot, affected, new WeakMap())
        ) {
          // not changed
          return lastSnapshot.current;
        }
        // eslint-disable-next-line no-catch-all/no-catch-all -- this is a performance optimization, if it fails for some reason, we can just fall back on React doing its thing with the new snapshot
      } catch {
        // ignore if a promise or something is thrown
      }
      return nextSnapshot;
    },
    // enable this if we ever turn on SSR for some reason:
    // () => snapshot(proxyObject),
  );

  inRender = false;
  React.useLayoutEffect(() => {
    lastSnapshot.current = currSnapshot;
    renderTracker.current = false;
  });

  const proxyCache = React.useMemo(() => new WeakMap<object, unknown>(), []); // per-hook proxyCache
  return createProxyToCompare(currSnapshot, affected, proxyCache, targetCache);
}
